<template>
  <v-row>
    <v-col cols="12">
      <div class="energy-boxes">
        <v-card :loading="loading">
          <v-card-text>
            <div class="header">
              <span class="title">Grid Consumption (AC)</span>
              <v-icon>mdi-lightning-bolt</v-icon>
            </div>
            <div class="mt-3 content">
              <div>
                <span class="value">{{ list?.grid_consumption || 0 }}</span>
                <span class="unit">kWh</span>
              </div>
              <div class="sub-text">
                +2.4% vs last period
              </div>
            </div>
          </v-card-text>
        </v-card>
        <v-card :loading="loading">
          <v-card-text>
            <div class="header">
              <span class="title">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                    >
                      <span>Charger Output (DC)</span>
                      <span
                        class="mdi ml-2 mdi-information-variant-circle-outline"
                      />
                    </div>
                  </template>
                  <span> The total energy the chargers have delivered. </span>
                </v-tooltip>
              </span>
              <v-icon>mdi-ev-station</v-icon>
            </div>
            <div class="mt-3 content">
              <div>
                <span class="value">{{ list?.charger_output || 0 }}</span>
                <span class="unit">kWh</span>
              </div>
              <div class="sub-text">
                93% Conversion Utilization
              </div>
            </div>
          </v-card-text>
        </v-card>
        <v-card :loading="loading">
          <v-card-text>
            <div class="header">
              <span class="title">System Efficiency</span>
              <v-icon>mdi-check-decagram</v-icon>
            </div>
            <div class="mt-3 content">
              <div>
                <span class="value">{{
                  list?.conversion_efficiency || 0
                }}</span>
                <span class="unit">%</span>
              </div>
              <div class="sub-text">
                Optimal Performance
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-col>
    <v-col
      cols="12"
      md="12"
    >
      <div>
        <v-card
          :loading="loading"
          class="my-0"
        >
          <v-card-text>
            <div class="px-2 py-2">
              <div class="powerflow-header">
                <span class="head">Power Flow</span>
                <span class="text-muted">AC Input vs DC Output (MW)</span>
              </div>
              <section class="mt-3 d-flex flex-column gap-1">
                <div>
                  <div class="d-flex justify-space-between">
                    <span>Site AC Input</span>
                    <span>{{ list.site_ac_input }} MW </span>
                  </div>
                  <v-progress-linear
                    :value="list?.site_ac_input"
                    color="success"
                    height="20"
                    striped
                  />
                </div>
                <div>
                  <div class="d-flex justify-space-between">
                    <span>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                          >
                            <span>Aggregated DC Output</span>
                            <span
                              class="mdi ml-2 mdi-information-variant-circle-outline"
                            />
                          </div>
                        </template>
                        <span>
                          The combined average power the chargers put out.
                        </span>
                      </v-tooltip>
                    </span>
                    <span>{{ list?.aggregated_dc_output }} MW </span>
                  </div>
                  <v-progress-linear
                    :value="list?.aggregated_dc_output"
                    color="success"
                    height="20"
                    striped
                  />
                </div>
              </section>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div class="mt-5">
        <v-card
          :loading="loading"
          class="my-0"
        >
          <v-card-text>
            <div class="px-2 py-2">
              <div class="energygap-header">
                <span class="head">Energy Gap (Loss) Over Time</span>
                <span class="text-muted">Efficiency Trends & Analysis</span>
              </div>

              <section class="mt-3">
                <apexchart
                  type="area"
                  height="280"
                  :options="chartOptions"
                  :series="series"
                />
              </section>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: "area",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit",
        },
        colors: ["#00bfa5"],
        stroke: {
          curve: "smooth",
          width: 2,
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 4,
        },
        xaxis: {
          categories: [
            "00:00",
            "02:00",
            "04:00",
            "06:00",
            "08:00",
            "10:00",
            "12:00",
            "14:00",
            "16:00",
            "18:00",
            "20:00",
            "22:00",
            "23:00",
            "23:30",
            "24:00",
          ],
          labels: { style: { colors: "#9e9e9e" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#9e9e9e" },
            formatter: (val) => `${val} kWh`,
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} kWh`,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      loading: "energyAnalyzer/getLoading",
      list: "energyAnalyzer/getDashboardData",
    }),
    series() {
      return [
        {
          name: "Energy Gap",
          data: this.list?.energy_gap_trend || [
            0, 2, 5, 9, 5, 10, 3, 5, 0, 0, 1, 8, 2, 9, 0,
          ],
        },
      ];
    },
  },
};
</script>

<style lang="sass" scoped>
.energy-boxes
  display: flex
  justify-content: space-between
  flex-wrap: wrap
  gap: 1rem


.energy-boxes .v-card
  flex: 1 1 calc(25% - 16px) /* Adjust the width and spacing as needed */
  margin: 0

.header
  display: flex
  justify-content: space-between
  align-items: center
  .title
    font-size: 1rem !important
    color: gray !important
    font-weight: 600

.content
    color: gray
    .value
        color: black
        font-size: 2rem
        font-weight: 700
        padding-right: 1rem
    div:last-child
        font-size: 0.875rem

.powerflow-header
  display: flex
  justify-content: space-between
  align-items: center
  .head
    font-size: 1.5rem

.energygap-header
  display: flex
  flex-direction : column
  justify-content: space-between
  .head
    font-size: 1.5rem



@media only screen and (max-width: 960px)
    .energy-boxes
        flex-direction: column
</style>
